// frontend/src/types.ts

export enum UserRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  orgScope?: string | null; // выбор в шапке: id организации или 'all'
}

// Сводный режим: все организации аккаунта
export const ALL_ORGS = 'all';

export enum CounterpartyType {
  LEGAL_ENTITY = 'LEGAL_ENTITY',
  ENTREPRENEUR = 'ENTREPRENEUR',
  INDIVIDUAL = 'INDIVIDUAL',
  STAFF = 'STAFF',
}

// Контрагенты, сотрудники и самозанятые — общие для всех организаций аккаунта
export interface Counterparty {
  id: string;
  type: CounterpartyType;
  name: string;
  employedByOrgId?: string | null; // сотрудник: где официально оформлен
  
  // Общие и налоговые
  inn?: string;
  kpp?: string;
  ogrn?: string;
  ogrnip?: string;
  registrationDate?: string | null; // ИП: дата присвоения ОГРНИП (ДатаОГРНИП покупателя в XML счёта-фактуры)
  contractNumber?: string;
  contractDate?: string;
  
  // Контакты
  phone?: string; // <-- НОВОЕ
  email?: string; // <-- НОВОЕ

  // Адреса
  legalAddress?: string;
  actualAddress?: string;
  directorName?: string;
  
  // Данные физлица / сотрудника
  birthDate?: string;  // <-- НОВОЕ
  birthPlace?: string; // <-- НОВОЕ

  passportSeries?: string;   // <-- НОВОЕ
  passportNumber?: string;   // <-- НОВОЕ
  passportIssuedBy?: string; // <-- НОВОЕ
  passportDate?: string;     // <-- НОВОЕ
  passportCode?: string;     // <-- НОВОЕ

  snils?: string;
  position?: string;
  isSelfEmployed?: boolean;
  npdRegistrationDate?: string;
  
  // Банк
  bankName?: string;
  bik?: string;
  accountNumber?: string;
  correspondentAccount?: string;
  cardNumber?: string | null;

  // Сотрудник: приём, увольнение, оклад
  hireDate?: string | null;
  dismissalDate?: string | null;
  salaryAmount?: number | null;
  tabNumber?: string | null;        // табельный номер
  militaryLiable?: boolean;         // военнообязанный — чек-лист приёма и задача «военкомат»
  hrMarks?: Record<string, string> | null;  // отметки чек-листа (инструктажи, СИЗ): ключ → дата
  typeHistory?: { type: string; from?: string; to?: string; organizationId?: string }[] | null;  // перевод в штат

  // Самозанятый: последняя проверка статуса НПД в ФНС
  npdStatus?: 'ACTIVE' | 'INACTIVE' | 'ERROR' | null;
  npdCheckedAt?: string | null;
  npdMessage?: string | null;
  // Роли по движению денег и пометка переноса данных (только чтение)
  isCustomer?: boolean;
  isSupplier?: boolean;
  archivedAt?: string | null;  // в архиве (уволен / больше не работаем) — 25.10.2026
  reviewNote?: string | null;
  previousNames?: string | null; // прежние ФИО через «;» (смена фамилии)

  files: string[];
}

// Вид контрагента для справочника: самозанятый — INDIVIDUAL с isSelfEmployed
export type CpKind = 'LEGAL' | 'IP' | 'PERSON' | 'SELF_EMPLOYED' | 'STAFF';
export const CP_KIND_LABELS: Record<CpKind, string> = { LEGAL: 'Юрлицо', IP: 'ИП', PERSON: 'Физлицо', SELF_EMPLOYED: 'Самозанятый', STAFF: 'Сотрудник' };
export const cpKind = (c: Counterparty): CpKind => c.type === CounterpartyType.LEGAL_ENTITY ? 'LEGAL'
  : c.type === CounterpartyType.ENTREPRENEUR ? 'IP' : c.type === CounterpartyType.STAFF ? 'STAFF' : c.isSelfEmployed ? 'SELF_EMPLOYED' : 'PERSON';

export interface SelfEmployedStatus {
  npdStatus: 'ACTIVE' | 'INACTIVE' | 'ERROR' | null;
  npdMessage: string | null;
  npdCheckedAt: string | null;
  formerEmployee: { counterpartyId: string; name: string; organizationId: string | null; dismissalDate: string | null; text: string } | null;
}

// ЭДО: задание и акт по выплате на подписи в «LiЧате» (backend/edo.py)
export type SignStatus = 'SENT' | 'SIGNED' | 'REJECTED' | 'ERROR' | 'CHANGED';
export interface SignInfo { signStatus?: SignStatus | null; signAt?: string | null; signNote?: string | null; signFile?: string | null }
export interface SEPayout extends SignInfo { id: string; date: string; amount: number; receiptRef: string | null; receiptFiles: string[]; hasReceipt: boolean;
                            paymentMethod: string | null; organizationId: string }
export interface SETask {
  id: string; number: number; date: string; dueDate: string | null; projectId: string | null; description: string; fileName: string | null;
  lines: { id: string; name: string; amount: number; qty: number | null; unit: string }[];
  total: number; acted: number; paid: number; status: 'IN_PROGRESS' | 'ACTED' | 'PAID';
  acts: { id: string; number: string; date: string; amount: number; fileName: string | null }[];
  payouts: SEPayout[];
}
export interface SEContract {
  id: string; organizationId: string; counterpartyId: string; number: string; date: string; validTo: string | null;
  kind: 'SERVICE' | 'VOLUME'; subject: string; fileName: string | null; tasks: SETask[]; nextTaskNumber: number;
  // Восстановлен по выпискам: «Действует с» и выплаты по договору без задания
  reconstructed: boolean; effectiveFrom: string | null; reviewNote: string | null; payouts: SEPayout[]; paid: number;
  edo: boolean;  // с разделом «Электронный документооборот» — все договоры, созданные с 16.10.2026
  scanFile: string | null; scanUploadedBy: string | null; scanUploadedAt: string | null;  // скан подписанного договора

}
// Сколько должны человеку по CRM (подсказка в окне выплаты), месяц — текущий
export interface CrmBalance { excluded: boolean; previousBalance: number; paid: number; previousLeft: number; toPay: number | null; remaining: number | null }
export interface CrmBalances { available: boolean; error: string | null; month: string | null; items: Record<string, CrmBalance> }
export interface SECard extends SelfEmployedStatus { contracts: SEContract[]; payoutsWithoutTask: SEPayout[] }

// Начисления сотрудникам (backend/tax.py ACCRUAL_TYPES)
export type AccrualType = 'SALARY' | 'BONUS' | 'VACATION' | 'SICK' | 'UNUSED_VACATION' | 'MATERIAL_AID';
export const ACCRUAL_LABELS: Record<AccrualType, string> = {
  SALARY: 'Зарплата', BONUS: 'Премия разовая', VACATION: 'Отпускные', SICK: 'Больничный (3 дня за счёт работодателя)',
  UNUSED_VACATION: 'Компенсация за неиспользованный отпуск', MATERIAL_AID: 'Материальная помощь',
};

export interface VacationEstimate {
  base: number; months: { month: string; amount: number; days: number }[]; workedMonths: number; calendarDays: number;
  avgDaily: number; days: number; gross: number; payBy: string; end: string;
}

export type HrDocKind = 'EMPLOYMENT_CONTRACT' | 'ORDER_HIRE' | 'ORDER_BONUS' | 'VACATION_REQUEST' | 'ORDER_VACATION' | 'CALC_VACATION'
  | 'DISMISSAL_REQUEST' | 'ORDER_DISMISSAL' | 'CALC_DISMISSAL' | 'INCOME_CERT'
  | 'HIRE_REQUEST' | 'SALARY_CARD_REQUEST' | 'PD_CONSENT' | 'GPH_TERMINATION';
export type HrDocStatus = 'DRAFT' | 'GENERATED' | 'SENT' | 'SIGNED';
export interface HrDocument { id: string; kind: HrDocKind; title: string; organizationId: string; counterpartyId: string; number: string;
                              date: string; data: Record<string, any>; transactionId: string | null; fileName: string | null;
                              status: HrDocStatus; sentAt: string | null; scanFile: string | null; scanUploadedBy: string | null;
                              scanUploadedAt: string | null; originalAtAccountant: boolean }
export type OrgHrKind = 'STAFFING' | 'VACATION_SCHEDULE' | 'TIMESHEET' | 'REG_PAY' | 'REG_PD' | 'REG_RULES';
export interface OrgHrDocument { id: string; kind: OrgHrKind; title: string; organizationId: string; number: string; date: string;
                                 data: Record<string, any>; fileName: string | null; status: HrDocStatus; sentAt: string | null;
                                 scanFile: string | null; scanUploadedBy: string | null; scanUploadedAt: string | null; originalAtAccountant: boolean }
export interface OrgHrOverview { kinds: { kind: OrgHrKind; title: string }[]; documents: OrgHrDocument[];
                                 staff: { id: string; name: string; position: string | null; tabNumber: string | null; salary: number; hireDate: string | null }[] }
export interface HrChecklistItem { key: string; title: string; kind: HrDocKind | null; required: boolean;
                                   status: HrDocStatus | 'MARKED' | 'MISSING'; docId: string | null; number: string | null; markedOn: string | null }
export interface HrChecklistEvent { event: 'HIRE' | 'VACATION' | 'DISMISSAL' | 'BONUS'; title: string; transactionId?: string; items: HrChecklistItem[] }
export interface Task { id: string; kind: string; title: string; dueDate: string; done: boolean; organizationId: string | null;
                        counterpartyId: string | null; refId: string | null }

export interface ClosingCheck { appendixId: string; number: number; contractNumber: string; organizationId: string; counterpartyId: string;
                                date: string; closingDocs: ClosingDocs; acts: Act[]; reasons: string[] }

export enum TransactionType {
  INCOME = 'INCOME',
  EXPENSE = 'EXPENSE',
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
}

export enum DocType {
  INVOICE = 'Счет',
  INVOICE_FACTURA = 'Счет-фактура',
  UPD = 'УПД',
  ACT = 'Акт выполненных работ',
  WAYBILL = 'Товарная накладная',
  KS2 = 'КС-2',
  KS3 = 'КС-3',
  AGREEMENT_APP = 'Приложение к договору',
  SCAN = 'SCAN',
}

// Вид операции для налогового учёта (см. backend/tax.py)
// SALARY — официальная ЗП, UNOFFICIAL_SALARY — без налогов, DIVIDENDS — только ООО,
// OWNER_WITHDRAWAL — вывод себе (только ИП), TAX — пополнение ЕНС или платёж в СФР
export type TransactionKind = 'REGULAR' | 'SALARY' | 'UNOFFICIAL_SALARY' | 'DIVIDENDS' | 'OWNER_WITHDRAWAL' | 'TAX' | 'NON_TAX' | 'INTERNAL_TRANSFER';
export type TransferBasis = 'LOAN' | 'LOAN_REPAYMENT' | 'OWN_FUNDS' | 'UNCLEAR' | 'PARTNER';
export type PaymentMethod = 'bank' | 'cash';
export type TaxAccount = 'ENS' | 'SFR';

export type PayoutLabel = 'SALARY' | 'BONUS';      // только для внутреннего учёта
export type SalaryPart = 'ADVANCE' | 'FINAL';      // аванс 25-го / расчёт 10-го
export type ObligationType = 'NDFL' | 'CONTRIBUTIONS' | 'INJURIES' | 'USN' | 'VAT' | 'IP_FIXED' | 'IP_1PCT';

// Налог к уплате (GET /obligations/{orgId}); гасится оплатой со счёта или зачётом из сальдо ЕНС
export interface TaxObligation {
  id: string;
  organizationId: string;
  type: ObligationType;
  periodStart: string | null;
  dueDate: string;
  amount: number;
  description: string;
  status: 'PENDING' | 'SETTLED';
  settledOn: string | null;
  settleMethod: 'ENS' | 'BANK' | null;
  paymentTransactionId: string | null;
  calculatedAmount?: number | null;  // расчёт приложения
  declaredAmount?: number | null;    // начислено по уведомлению / декларации — к уплате по нему
  declaredSource?: string | null;
}

// Расчёт приложения против поданного (уведомления / декларации) с расшифровкой по приходам
export interface DeclaredDiff {
  total: number;
  hasDeclared: boolean;
  items: { type: ObligationType; periodStart: string | null; calculated: number; declared: number; diff: number; other: number;
           operations: { id: string; date: string; amount: number; description: string; effect: number }[] }[];
}

export interface PayrollPreview {
  items: { counterpartyId: string; net: number; gross: number; ndfl: number; contribBase?: number;
           monthGross: number | null; monthContributions: number | null; monthInjuries: number | null }[];
  ndflDue: string;
  contributionsDue: string | null;
  injuriesDue: string | null;
}

export interface GeneratedDocument {
  id: string;
  type: DocType;
  fileName: string;
  dateCreated: string;
  isOriginalUploaded: boolean;
}

export interface TransactionItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  price: number;
  amount: number;
}

export interface Transaction extends SignInfo {
  id: string;
  // Передача выплаты самозанятому в зарплатный модуль lan-install (backend/crm.py)
  crmSyncStatus?: 'SENT' | 'ERROR' | 'SKIPPED' | 'DELETED' | null; crmSyncError?: string | null; sendToCrm?: boolean;
  organizationId: string;
  date: string;
  type: TransactionType;
  counterpartyId?: string;
  amount: number;
  category?: string;
  description: string;
  status: TransactionStatus;
  
  hasVat: boolean;
  vatRate: number;
  userId?: string;

  // Не передан -> бэкенд определит по категории и контрагенту
  kind?: TransactionKind;
  // Есть подтверждающий документ (акт, чек «Мой налог»). Без него расход не уменьшает налоговую базу
  hasDocument?: boolean;
  paymentMethod?: PaymentMethod | null;
  bankAccountId?: string | null;
  taxAccount?: TaxAccount | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  transferGroupId?: string | null; // перевод между организациями: пара операций
  transferBasis?: TransferBasis | null;
  projectId?: string | null;
  // Перенос данных: договор выплаты, источник (bank / table / bank+table) и пометка «проверить»
  contractId?: string | null;
  importSource?: string | null;
  reviewNote?: string | null;
  incomeBasis?: string | null; // приход «не доход»: основание (INCOME_BASES)
  sourceRow?: number | null;   // строка листа «ПриходыЗП» таблицы «Мани»
  planningYear?: number | null; // планирование какого года (стык 2025/2026: в переносе прошлого года)

  // Выплаты
  payoutLabel?: PayoutLabel | null;
  bonusAmount?: number | null;     // из выплаты «ЗП» — премия (внутренний учёт и планирование)
  salaryPart?: SalaryPart | null;
  salaryPeriod?: string | null; // YYYY-MM-01 — месяц, за который зарплата
  accrualType?: AccrualType | null; // вид начисления сотруднику; пусто — зарплата
  grossAmount?: number | null;      // сумма «начислено», если вводили её
  periodFrom?: string | null;       // отпуск / больничный
  periodTo?: string | null;
  days?: number | null;
  appendixId?: string | null;       // самозанятый: задание
  receiptRef?: string | null;       // чек «Мой налог»

  documents: GeneratedDocument[];
  items: TransactionItem[];
}

type KindTotals = Record<TransactionKind, number>;

export interface TaxPeriod {
  quarter: number;
  income_base: number;
  expense_base: number;
  tax_base: number;
  tax_calculated: number;
  reduction: number;
  tax: number;
  due_for_period: number;
  own_contributions: number;
}

// Ответ GET /reports/{orgId}?year=
export interface TaxReport {
  year: number;
  regime: 'OSNO' | 'USN6' | 'USN15';
  management: {
    balance: number;
    pending_income: number;
    cash_in: number;
    cash_out: number;
    cash_in_by_kind: KindTotals;
    cash_out_by_kind: KindTotals;
    revenue_net: number;
    expenses: number;
    expenses_undocumented: number;
    labor_cost: number;
    own_contributions: number;
    main_tax: number;
    net_profit: number;
    dividends_paid: number;
    withdrawals: number;
    cash_out_by_method: Record<PaymentMethod, number>;
    cash_in_by_method: Record<PaymentMethod, number>;
    transfers_in: number;
    revenue_bank: number;   // выручка от контрагентов (безнал)
    revenue_cash: number;   // выручка наличными
    other_in: number;       // прочие поступления — не доход
    movements: { label: string; in: number; out: number }[];  // перемещения между счетами и кошельками
    expense_rows: Record<string, { bank: number; cash: number }>;  // таблица «Расходы»: строка → безнал / наличные
    undocumented: { count: number; amount: number };   // расходы без документов (кроме самозанятых)
    se_no_receipt: { count: number; amount: number };  // выплаты самозанятым без чека
    self_employed_count: number;
    top_recipients: { counterparty_id: string; amount: number }[];
    transfers_out: number;
    wallets: WalletBalance[];
    unassigned_balance: number;
  };
  tax: {
    vat_mode: VatMode;
    vat_rate: number;
    income_base: number;
    expense_base: number;
    tax_base: number;
    tax_calculated: number;
    reduction: number;
    reduction_cap_percent: number;
    has_employees: boolean;
    main_tax: number;
    ip_fixed: number;
    ip_1pct: number;
    vat_out: number;
    vat_in: number;
    vat_to_pay: number;
    excluded: { undocumented_expenses: number; unofficial_salary: number; withdrawals: number; dividends: number; non_tax_in: number; non_tax_out: number };
    periods: TaxPeriod[];
    vat_by_quarter: { quarter: number; vat_out: number; vat_in: number; vat_to_pay: number }[];
  };
  payroll: {
    salary_net: number;
    salary_unofficial: number;
    salary_gross: number;
    dividends_net: number;
    dividends_gross: number;
    ndfl: number;
    ndfl_by_rate: Record<string, number>;
    contributions: number;
    injuries: number;
    injuries_rate_percent: number;
    per_transaction: Record<string, { kind: TransactionKind; net: number; gross: number; ndfl: number; period: string; withheld_on: string }>;
  };
  taxes_paid: number;
  // Налоги к уплате и ЕНС (tax_obligations)
  obligations: {
    accrued_year: number;
    payable_by_type: Record<string, number>;     // к уплате за год: по уведомлению, где есть, иначе расчёт
    calculated_by_type: Record<string, number>;  // расчёт приложения
    declared_types: string[];
    settled_year: number;
    overdue: number;
    overdue_count: number;
    due_30_days: number;
    future: number;
    unpaid_total: number;
    nearest: { type: ObligationType; title: string; amount: number; due_date: string } | null;
    ens_wallet: number;
    reserve: number;          // резерв ИП: 1% поступлений за год
    ens_topups_year: number;
    available: number; // можно забрать себе
  };
  assumptions: string[];
}

// Кошелёк организации: расчётный счёт или касса наличных; остаток на начало — «остаток с 2025»
export interface BankAccount {
  id: string;
  kind?: 'bank' | 'cash';
  name?: string | null;
  bankName: string;
  bik: string;
  accountNumber: string;
  correspondentAccount: string;
  openingBalance?: number;
  openingDate?: string | null;
}

export interface CompanyProfile {
  id: string;
  name: string;
  type: 'LEGAL_ENTITY' | 'ENTREPRENEUR';
  inn: string;
  kpp: string;
  ogrn: string;
  ogrnip?: string;
  registrationDate?: string | null;  // дата регистрации ИП/ООО (в счёте-фактуре и XML для ЭДО)
  vatInvoiceNextNumber?: number | null;  // следующий номер счёта-фактуры (переход с 1С) в году vatInvoiceNextYear
  signatureFile?: string | null;  // факсимиле подписи (PNG без фона) — для PDF «с подписью»
  stampFile?: string | null;      // печать
  signatureLayout?: SignatureLayout | null; // положение подписи на линии, мм
  closedUntil?: string | null;  // период закрыт по эту дату (переход на приложение): операции до неё не меняются
  vatInvoiceNextYear?: number | null;
  legalAddress: string;
  actualAddress: string;
  directorName: string;
  taxSystem: string;
  accounts: BankAccount[];
  activeAccountId: string | null;
  ownerInn?: string | null;       // ООО: ИНН владельца (внесение собственных средств)
  isMsp: boolean;
  mspPriority?: boolean;          // приоритетная отрасль (тариф МСП с 2026)
  advanceDay?: number;            // день аванса за текущий месяц
  settlementDay?: number;         // день расчёта за прошлый месяц
  payrollRounding?: 'HALF_UP' | 'DOWN';
  edoEnabled?: boolean;           // ЭДО с самозанятыми: подпись заданий и актов в «LiЧате» кодом из email
  // Нумерация счетов: одна серия, своя на расчётный счёт или на контрагента
  invoiceNumbering?: 'ORG' | 'ORG_ACCOUNT' | 'ORG_COUNTERPARTY';
  // ИП на УСН «доходы»: уменьшать авансы I–III кв. на 1% текущего года (в налоге за год — всегда)
  usnReduce1pct?: boolean;
  injuriesRates?: InjuriesRate[]; // тарифы на травматизм по годам
  vatModes?: VatModeSetting[];    // режим НДС на УСН по годам
}

// Режим НДС на УСН: освобождение, 5%, 7% или общая ставка (20% до 2026, 22% с 2026) с вычетами
export type VatMode = 'EXEMPT' | 'USN_5' | 'USN_7' | 'GENERAL';
export interface VatModeSetting {
  year: number;
  mode: VatMode;
}

// Тариф на травматизм: действует с года year до года следующей записи
export interface InjuriesRate {
  year: number;
  ratePercent: number;
}

export interface AppState {
  user: User | null;
  users: User[];
  counterparties: Counterparty[];
  transactions: Transaction[];
  organizations: CompanyProfile[];
  currentOrganizationId: string;
}

export interface WalletBalance { id: string; name: string; kind: 'bank' | 'cash'; opening: number; balance: number }

// GET /reports/all — сводная таблица по организациям (налоги по каждой отдельно, в «Итого» — сумма)
export type ConsolidatedKey = 'cash_in' | 'cash_in_bank' | 'cash_in_cash' | 'cash_out' | 'cash_out_bank' | 'cash_out_cash'
  | 'salary_official' | 'salary_unofficial' | 'taxes_accrued' | 'taxes_paid' | 'balance' | 'available' | 'net_profit'
  | 'unpaid_taxes' | 'ens_wallet' | 'reserve' | 'revenue_bank' | 'revenue_cash' | 'other_in';
export interface MutualDebt { lenderOrgId: string; borrowerOrgId: string; amount: number }
export interface ConsolidatedReport {
  year: number;
  organizations: { id: string; name: string; values: Record<ConsolidatedKey, number>; wallets: WalletBalance[] }[];
  totals: Record<ConsolidatedKey, number>;
  debts: MutualDebt[];
}

export interface TransactionChange {
  id: number;
  transactionId: string;
  userId: string | null;
  at: string;
  action: 'create' | 'update' | 'delete';
  changes: Record<string, [unknown, unknown]>;
}

// --- Объекты, приложения, счета и оплаты (модель Ланстроя) ---
export type ProjectStatus = 'IN_PROGRESS' | 'DONE' | 'CLOSED';
export interface ProjectMetrics {
  done: number; formalized: number; invoiced: number; paid: number; notFormalized: number; notInvoiced: number;
  debt: number; opening: number; costs: number; paidNet: number; margin: number;
  paidDirect: number; unpaid: number;  // оплачено прямо на объект; выполнено − все оплаты (минус — переплата)
  requests: number;  // заявки с сайта (оценка по прайсу) — входят в estimate
  inVolumes: number;  // висит в «Объёмах»: сделано по заявкам, не выставлено заказчику (с НДС)
  estimate: number;  // оценки («примерно», заявки с сайта) — в «Выполнено» и долг не входят
  paidPool: number; poolPending: number;  // зачтено из пула взаимозачёта / ждёт решения
  poolReceived: number; poolOffset: number; poolRest: number; poolQuestion: number;  // для объекта-пула
}
export interface Project {
  id: string; name: string; address: string | null; customerId: string | null; endCustomerId: string | null;
  status: ProjectStatus; comment: string | null; metrics: ProjectMetrics; organizationIds?: string[];
  requestsFrom?: string | null;  // с этой даты заявки с сайта показываются оценкой
  isPool?: boolean;  // пул взаимозачёта («Переплата Рязань»)
  works?: WorkItem[]; appendices?: Appendix[]; invoices?: Invoice[]; feed?: FeedItem[];
  transactionIds?: string[]; payments?: { month: string; amount: number }[];
}
export interface EndCustomer { id: string; name: string; counterpartyId: string | null }
export interface Contract {
  id: string; organizationId: string; counterpartyId: string; number: string; date: string; kind: ContractKind;
  paymentDays: number; paymentBasis: 'ACT' | 'INVOICE'; purposeTemplate: string; workDescription: string;
  appendixCount: number; nextAppendixNumber: number; subject: string; monthlyAmount: number | null; fileName: string | null;
  closingDocs: ClosingDocs;
  // ТЗ v5: исходная цена и сроки; state — текущие с подписанными доп. соглашениями
  amount?: number | null; startDate?: string | null; endDate?: string | null; state?: ContractState;
}
export interface ContractState {
  baseAmount: number | null; currentAmount: number | null; pendingDelta: number; baseEndDate: string | null;
  currentEndDate: string | null; currentStartDate: string | null; agreementCount: number; nextAgreementNumber: number;
  history: { number: number; date: string; delta: number; amountBefore: number | null; amountAfter: number | null; endDate: string | null }[];
}
export type AgreementMode = 'CHANGE' | 'NEW' | 'TERMS';
export type AgreementKind = 'add_works' | 'change_scope' | 'change_terms' | 'other';
export type AgreementStatus = 'DRAFT' | 'SENT' | 'SIGNED' | 'CANCELLED';
export const AGREEMENT_STATUS: Record<AgreementStatus, string> = { DRAFT: 'черновик', SENT: 'отправлено', SIGNED: 'подписано', CANCELLED: 'отменено' };
export interface Agreement {
  id: string; contractId: string; contractNumber: string; contractDate: string; organizationId: string; counterpartyId: string;
  number: number; date: string; mode: AgreementMode; kinds: AgreementKind[]; appendixId: string | null; appendixNumber: number | null;
  newStartDate: string | null; newEndDate: string | null; effectiveFrom: string | null; effectiveText: string;
  deltaNet: number; deltaVat: number; deltaTotal: number; vatRate: number; otherText: string; paymentTerms: string;
  objectAddress: string; contractClause: string; status: AgreementStatus; sentAt: string | null; scanFile: string | null;
  fileName: string | null; scanOverdue: boolean; title: string; hasGoods: boolean; warning?: string | null;
  changes: { line: string; name: string | null; before: { qty: number | null; amountNet: number } | null; after: { qty: number | null; amountNet: number } | null }[];
  lines: { id: string; name: string; qty: number | null; unit: string | null; amountNet: number; section: string | null; itemType: string | null }[];
}
export type EstimateItemType = 'WORK' | 'GOODS';
export interface EstimateRow { row?: number; name: string; qty: number; unit: string; unitGuessed?: boolean; price: number | null; amount: number; itemType: EstimateItemType }
export interface EstimateSection { key: string; title: string; rows: EstimateRow[]; total: number; fileTotal: number | null; ok: boolean }
export interface EstimateParse {
  uploadId: string; originalName: string | null; sheets: string[]; sheet: string; preview: (string | number)[][];
  headerRow: number | null; columns: Record<string, number> | null; sections: EstimateSection[]; total?: number; fileTotal?: number | null;
  totalOk?: boolean; notes: string[]; vatHint: 'with' | 'without' | null; warnings: string[]; mappingRemembered?: boolean;
}
export interface EstimateVersion {
  id: string; version: number; fileName: string; originalName: string | null; sheet: string | null; uploadedAt: string | null; rows: number; total: number;
  diff: { added: { name: string }[]; removed: { name: string }[]; changed: { name: string; before: { qty: number; price: number; amount: number }; after: { qty: number; price: number; amount: number } }[] } | null;
}
export type ClosingDocs = 'ACT' | 'UPD' | 'KS';
export const CLOSING_DOCS: Record<ClosingDocs, string> = { ACT: 'Акт (+ СФ, если НДС)', UPD: 'УПД (статус 1 / 2)', KS: 'КС-2 + КС-3 (+ СФ, если НДС)' };
export type SignedDocType = 'APPENDIX' | 'INVOICE' | 'ACT' | 'VAT_INVOICE' | 'UPD' | 'KS2' | 'KS3' | 'CONTRACT' | 'OTHER';
export const SIGNED_DOC_TYPES: Record<SignedDocType, string> = {
  ACT: 'Акт', UPD: 'УПД', VAT_INVOICE: 'Счёт-фактура', KS2: 'КС-2', KS3: 'КС-3', APPENDIX: 'Приложение', INVOICE: 'Счёт',
  CONTRACT: 'Договор', OTHER: 'Другое',
};
export interface AppendixFile { id: string; docType: SignedDocType; refId: string | null; fileName: string; originalName: string | null; uploadedAt: string | null; uploadedBy: string | null }
export type ContractKind = 'VOLUME' | 'SERVICE' | 'ONE_OFF' | 'FRAME';
export const CONTRACT_KINDS: Record<ContractKind, string> = {
  VOLUME: 'Подряд на объём работ', SERVICE: 'Обслуживание', ONE_OFF: 'Разовый', FRAME: 'Рамочный',
};
export type BillingStatus = 'NOT_FORMALIZED' | 'IN_APPENDIX' | 'INVOICED' | 'PARTIALLY_PAID' | 'PAID';
export interface WorkItem {
  id: string; projectId: string; organizationId: string | null; date: string; dateTo: string | null; description: string;
  amountNet: number; vatRate: number; amountGross: number; isEstimate: boolean; formalizedNet: number; remainingNet: number;
  status: BillingStatus; appendixIds: string[];
  pool?: { id: string; poolId: string; decision: PoolDecision; amount: number } | null;
}
export type PoolDecision = 'OFFSET' | 'SEPARATE_ACT' | 'PENDING';
export interface Invoice {
  id: string; organizationId: string; counterpartyId: string | null; appendixId: string | null; appendixNumber: number | null;
  contractId: string | null; projectIds: string[]; bankAccountId: string | null; number: string; date: string; amount: number;
  vatRate: number; vat: number; purpose: string; paid: number; remaining: number; status: BillingStatus; expectedDate: string;
  expectedManual: boolean; overdueDays: number; fileName: string | null;
  payments: { transactionId: string; amount: number; date: string | null }[];
}
export interface VatInvoice {
  id: string; organizationId: string; appendixId: string | null; kind: 'SHIPMENT' | 'ADVANCE'; form: 'SF' | 'UPD'; actId: string | null;
  transactionId: string | null; number: string; date: string; amount: number; vatRate: number; vat: number; fileName: string | null;
}
export interface Act { id: string; kind: ClosingDocs; number: string; ks3Number?: string | null; date: string; amount: number; vat: number;
                       fileName: string | null; vatInvoice: VatInvoice | null; extra?: boolean }
// Перечень адресов реализации из таблицы заказчика: по каждому адресу количество и сумма по услугам
export interface AppendixAddresses {
  services: { name: string; price: number; withVat: boolean }[];
  extra: string[];
  rows: { name: string; address: string; extra: (number | string)[]; qty: number[]; sum: number[] }[];
}

export interface AddressGroup { key: string; recipient: string; appendixNumber: number | null; total: number; rows: AppendixAddresses['rows'] }

export interface Appendix {
  id: string; addresses?: AppendixAddresses | null; contractId: string; contractNumber: string; organizationId: string; counterpartyId: string; number: number;
  date: string; vatRate: number; description: string; net: number; vat: number; gross: number; invoiced: number; paid: number;
  notInvoiced: number; debt: number; status: BillingStatus; fileName: string | null; projectIds: string[];
  lines: { id: string; workItemId: string | null; name: string | null; amountNet: number; description: string; projectId: string | null;
           qty: number | null; unit: string | null; section?: string | null; itemType?: EstimateItemType | null; saId?: string | null; saNumber?: number | null }[];
  // ТЗ v5: КП на согласовании, основание «по Доп. соглашению», доп. соглашения, переплата после уменьшения
  lifecycle?: 'QUOTE' | 'ACTIVE'; basisSaId?: string | null; basis?: string | null; hasGoods?: boolean; overpaid?: number; dueTotal?: number;
  quoteMeta?: { object?: string; validity?: string; payment?: string; warranty?: string; notes?: string[]; title?: string } | null;
  agreements?: { id: string; number: number; date: string; status: AgreementStatus; mode: AgreementMode; deltaTotal: number }[];
  contractClosingDocs: ClosingDocs; files: AppendixFile[];
  projectId: string | null; contractKind: ContractKind; createdBy: string | null; createdAt: string | null;
  invoices: Invoice[]; acts: Act[];
  acted: number; notActed: number; closed: boolean; advanceWithoutVatInvoice: number; vatInvoices: VatInvoice[];
  nextVatInvoiceNumber: string;
  nextActNumber?: string; // акт на всё приложение — номер приложения, если свободен
}
export interface Payment {
  id: string; organizationId: string; counterpartyId: string | null; date: string; amount: number; description: string;
  bankAccountId: string | null; allocated: number; unallocated: number; kind?: string;
  allocations: { id: string; invoiceId: string | null; openingDebtId: string | null; projectId: string | null; amount: number; included?: boolean }[];
  // Объект по номеру счёта/приложения из назначения; предупреждения («демонтаж» на объекте монтажа)
  hint?: { projectId: string; source: string } | null; warnings?: string[];
  // Сколько из оплаты пришлось на каждый объект (напрямую и через счета)
  projectShares: { projectId: string; amount: number }[];
  // Разнесено на счёт реализации без объекта — объект можно указать из «Оплат»
  noProject?: { invoiceId: string; number: string; appendixNumber: number | null; amount: number }[];
}
export interface ObjectReconSummary {
  counterpartyId: string; name: string; done: number; paidToProjects: number; unallocated: number; received: number;
  objectsDebt: number; objectsOverpaid: number; balance: number; unallocatedCount: number; poolRest?: number; debt?: number;
  inVolumes?: number;
}
// Сверка с заказчиком по объектам
export interface ObjectReconRow {
  projectId: string; name: string; status: string; endCustomer: string; done: number; paid: number; balance: number;
  estimate: number; paidPool: number; poolPending: number; inVolumes: number;
  works: { id: string | null; date: string; description: string; amount: number; estimate: boolean;
           pool: { id: string; decision: PoolDecision; amount: number } | null }[];
  payments: { transactionId: string | null; date: string; organizationId: string | null; amount: number; total: number;
              description: string; pool?: string }[];
}
export interface ObjectReconPool {
  projectId: string; name: string; received: number; question: number; offset: number; pending: number; separate: number; rest: number;
  sources: { allocationId: string; transactionId: string; date: string; organizationId: string; amount: number; total: number;
             description: string; included: boolean }[];
  offsets: { id: string; workItemId: string; projectId: string; projectName: string; date: string; description: string;
             workAmount: number; amount: number; decision: PoolDecision; note: string }[];
}
export interface ObjectRecon {
  rows: ObjectReconRow[];
  payments: { id: string; date: string; organizationId: string; amount: number; cash: boolean; description: string;
              shares: { projectId: string; amount: number }[]; unallocated: number }[];
  pools: ObjectReconPool[];
  totals: { done: number; estimate: number; paidToProjects: number; paidMoney: number; paidPool: number; debt: number;
            unallocated: number; received: number; poolReceived: number; poolOffset: number; poolRest: number; poolPending: number;
            poolQuestion: number; objectsDebt: number; objectsOverpaid: number; balance: number; inVolumes: number };
  fileName?: string;
}
export interface PaymentCandidate extends Payment { byNumber: boolean; sameAmount: boolean; take: number }
export interface OpeningDebt {
  id: string; organizationId: string; counterpartyId: string; projectId: string | null; year: number; amount: number;
  paid: number; remaining: number; comment: string;
}
export interface FeedItem { date: string; kind: 'work' | 'appendix' | 'invoice' | 'act' | 'payment'; id: string; text: string; amount: number; organizationId?: string | null; estimate?: boolean }
export interface BillingSummary {
  opening: number; done: number; totalDue: number; invoiced: number; paid: number; paidInvoices: number; paidOpening: number; paidProjects?: number;
  notInvoiced: number; invoicedNotPaid: number; remaining: number; overpayment: number;
  byOrg: { organizationId: string; done?: number; invoiced?: number; paid?: number; unpaid: number }[];
  byEndCustomer: { endCustomerId: string | null; done?: number; invoiced?: number; paid?: number; unpaid: number }[];
}
export interface PaymentSchedule {
  expected: { invoiceId: string; number: string; organizationId: string; counterpartyId: string | null; date: string; amount: number; overdueDays: number }[];
  facts: { transactionId: string; organizationId: string; counterpartyId: string | null; date: string; amount: number }[];
  weeks: { week: string; expected: number; overdue: number; paid: number }[];
  next14: number; overdue: number;
}
export interface Debtor {
  customerId: string | null; customerName: string; debt: number; notFormalized: number; oldestUnpaidDate: string | null;
  oldestDays: number | null; byOrg: { organizationId: string; debt: number }[]; projectIds: string[];
}
export interface BankTxRef { id: string; date: string; amount?: number; counterparty?: string; description?: string; type?: string }
export interface BankRow {
  row: number; number: string; amount: number | null; purpose: string; payerName: string; payerInn: string; receiverName: string;
  receiverInn: string; status: 'green' | 'yellow' | 'grey'; notes: string[]; import: boolean;
  action: 'import' | 'link' | 'registry' | 'skip'; linkIds?: string[];
  allocations: { invoiceId: string; amount: number }[]; createCounterparty: { name: string; inn: string; type: string } | null;
  organizationId: string | null; bankAccountId: string | null; counterpartyId: string | null; counterpartyName?: string;
  type: 'INCOME' | 'EXPENSE' | null; kind: string | null; taxAccount: string | null; category: string | null;
  duplicate: 'exact' | 'possible' | null; date?: string;
  candidates?: { invoiceId: string; number: string; date: string; remaining: number }[];
  matches: BankTxRef[]; registry: { exact: boolean; total?: number; items: BankTxRef[] } | null;
  card: boolean; rule: string | null; resolved: string | null; saveRule?: { inn?: string; text?: string } | null;
  file?: string | null;  // из какой выписки (загрузка нескольких сразу)
  ai?: string | null;    // пояснение ИИ к применённой подсказке
}
export interface BankStatementInfo { organizationId: string; bankAccountId: string; account: string; dateFrom: string | null;
  dateTo: string | null; opening: number | null; closing: number | null; files?: number }
// Подключение ИП к банку по API (bankfeed.py)
export interface BankConnection {
  id: string; organizationId: string; bank: string; tokenHint: string | null; syncFrom: string; enabled: boolean;
  lastSyncAt: string | null; lastError: string | null; toReview: number;
  certExpires?: string | null; secretExpires?: string | null;  // Сбер: когда истекут сертификат и обычный client_secret
  lastResult: { fetched: number; new: number; created: number; linked: number; skipped: number; diff?: { wallet: string; diff: number | null }[] } | null;
}
// Подсказка ИИ по строке выписки (POST /bank/ai-suggest)
export interface BankAiRow { row: number; action: 'import' | 'skip'; kind: string | null; category: string | null; counterpartyId: string | null; note: string }
export interface BankHint { type: string; text: string; amount?: number; items?: (BankTxRef & { number?: string })[] }
export interface BankReconcile { bankAccountId: string; organizationId: string; wallet: string; dateFrom: string | null; dateTo: string;
  bankClosing: number | null; ours: number; diff: number | null; hints: BankHint[] }
export interface AiAnalysis { id: string; parentId: string | null; question: string | null; answer: string | null; error: string | null;
  model: string | null; inputTokens: number | null; outputTokens: number | null; createdAt: string; pending: boolean;
  kind?: 'chat' | 'bank_rows'; bank?: boolean }

// --- Планирование ---
export interface PlanningArticle { key: string; name: string; percent: number; type: 'category' | 'partner'; orgId?: string | null }
export interface PlanningVersion { id: string; validFrom: string; articles: PlanningArticle[]; mapping: Record<string, string | null> }
export interface PlanningSettings {
  versions: PlanningVersion[];
  carryovers: Record<string, Record<string, number>>;
  useCarryover: boolean; // «Учитывать перенос с прошлого года» (по умолчанию выкл.)
  priorYearTaxesToPartner: boolean; // налоги ИП за прошлые годы, уплаченные в текущем, — на статью партнёра (по умолчанию вкл.)
  vatBase: PlanningVatBase; // НДС в базе распределения
  vatBaseModes: Record<PlanningVatBase, string>;
  withdrawalSubtypes: Record<string, string>; // подвиды «Вывода себе»: '' — Бонус, SALARY — ЗП партнёра, BONUS — премия партнёра
  categories: Record<string, string>;
  pendingDecision: string[];
}
export interface PlanningRow {
  key: string; name: string; percent: number; type: 'category' | 'partner'; orgId?: string | null;
  plan: number; fact: number; carry: number; remaining: number; expected: number; overspent: boolean;
  planOther: number; // план при другом варианте НДС в базе
  table?: { plan?: number; carry?: number; fact?: number; left?: number } | null; // значения таблицы «Мани» (до корректировок)
  bonuses?: { table: number; taxes: number; transition: number; other: number; total: number }; // статьи партнёров
  remainingBeforeTransition?: number;
}
export type PlanningVatBase = 'ACTUAL' | 'TABLE';
export interface PlanningReport {
  from: string; to: string; incomeGross: number; vat: number; base: number; articles: PlanningRow[]; useCarryover: boolean; carrySource?: 'saved' | 'auto' | null;
  cashIn: number; // наличные приходы «не доход» («нал» таблицы) — в базе
  vatBase: PlanningVatBase; vatBaseModes: Record<PlanningVatBase, string>;
  bases: Record<PlanningVatBase, { base: number; vat: number; plan: Record<string, number> }>;
  tableBase?: number | null;
  totals: { plan: number; fact: number; carry: number; remaining: number; expected: number };
  monthly: { month: string; plan: number; fact: number }[];
  unmapped: { category: string; name: string; amount: number; pendingDecision: boolean }[];
}


// Блок «Как в таблице»: строки шапки листа «ПриходыЗП» — приложение, таблица, разница, причина
export interface SheetCompareCell { app: number | null; table: number | null; diff: number | null; note: string | null }
export interface SheetCompare {
  year: number;
  columns: { id: string; name: string }[];
  rows: { key: string; label: string; indent: number; reason: string; cells: Record<string, SheetCompareCell> }[];
}

// Основания прихода «не доход» (backend tax.INCOME_BASES)
export const INCOME_BASES: Record<string, string> = {
  OWNER_FUNDS: 'Личные средства владельца', PERSONAL_DEBT_RETURN: 'Возврат личного долга',
  PERSONAL_PROPERTY_SALE: 'Продажа личного имущества', ACCOUNTABLE_RETURN: 'Возврат подотчёта',
};

// «Разложить реестр»: платёж-реестр и выплаты из кошелька партнёра (строки «Мани») — кандидаты
export interface RegistrySplitData {
  id: string; date: string; amount: number; number: string | null;
  candidates: { id: string; date: string; amount: number; sourceRow: number | null; counterparty: string; selfEmployed: boolean; description: string }[];
}

// --- Объёмы работ: заявки CRM lan-install → реализации по ИП → выплаты самозанятым (backend/volumes.py) ---
export type RateRole = 'PANEL' | 'FIRMWARE' | 'LINK' | 'BUFFER' | null;
export interface WorkRate {
  id?: string; code: string; name: string; unit: string; clientPrice: number; contractorPrice: number; role: RateRole; position?: number;
  // request_param — параметр типа заявки CRM (название и список ведутся там), warehouse — склад, system, manual
  source?: 'request_param' | 'warehouse' | 'system' | 'manual'; isActive?: boolean; lanInstallParamId?: number | null;
  docName?: string | null;  // название в задании и акте самозанятому (пусто — как name)
  inVolumes?: boolean;      // false — позиция не учитывается в объёмах
}
export type LanMode = 'ITEMS' | 'MAN_DAY' | 'MANUAL';
export type LanStatus = 'NEW' | 'REVIEW' | 'FREE' | 'LINKED' | 'CANCELLED';
export interface LanType { id: string; crmTypeId: number; name: string; projectId: string | null; mode: LanMode; manDayRate: number | null }
export interface LanMapping { id?: string; source: 'WORK' | 'MATERIAL'; sourceName: string; code: string | null; factor: number }
export interface VolumeSettings {
  types: LanType[]; mappings: LanMapping[]; unmapped: { source: 'WORK' | 'MATERIAL'; sourceName: string }[]; codes: string[];
  crmConfigured: boolean; integrationKey: boolean;
}
export interface LanWork { id: string; source: string; sourceName: string; sourceQty: number; code: string | null; name: string | null; qty: number }
// Объект по адресам: сделано по заявкам «Объёмов» ↔ выставлено в реализациях (перечни адресов заказчика)
export type AddressState = 'partial' | 'unbilled' | 'over' | 'noRequests' | 'billed' | 'moved' | 'empty';
export interface AddressRow {
  key: string; address: string; name: string; state: AddressState;
  donePanels: number; doneLinks: number; billedPanels: number; billedLinks: number; billedSum: number;
  restPanels: number; restLinks: number; unbilled: number;
  rawPanels: number; rawLinks: number;  // по заявкам CRM, до поправки
  adjustment: { targetKey: string | null; donePanels: number | null; doneLinks: number | null; comment: string } | null;
  movedTo: string | null; movedToAddress: string | null;
  billing: { appendixId: string; number: number | null; date: string | null; organization: string; panels: number; links: number; sum: number }[];
  requests: { id: string; number: string | null; date: string; status: LanStatus; address: string | null; panels: number; links: number; movedFrom?: string }[];
}
export interface AddressLedger { addressMode: boolean; pricePanel: number; priceLink: number; totals: Record<string, number>; rows: AddressRow[]; }
export interface AddressCheck { rows: AddressRow[]; pricePanel: number; priceLink: number; withoutRequests: number; mismatch: number; }

export interface LanComment { id: number; text: string; author: string | null; createdAt: string | null; isClosing: boolean; photos: number; }
export interface LanComments { available: boolean; error: string | null; comments: LanComment[]; }

export interface LanRequest {
  id: string; externalTaskId: string | null; number: string | null; crmTypeId: number | null; typeName: string | null;
  address: string | null; workDate: string; closedAt: string | null; brigade: { id?: number; fio: string; leader?: boolean }[];
  crewSize: number | null; projectId: string | null; status: LanStatus; mode: LanMode | null; clientAmount: number | null;
  manualAmount: number | null; realizationId: string | null; changedAfterLink: boolean; comment: string | null; works: LanWork[];
}
export interface RealizationVolumes {
  id: string; number: number; date: string; contractId: string; contractNumber: string; organizationId: string;
  counterpartyId: string; projectId: string | null; gross: number; paid: number; status: BillingStatus; requests: number;
  volumes: { code: string; name: string; unit: string; qty: number }[];
  volumesAuto?: boolean;  // объём не указан явно — посчитан по строкам реализации
}
export interface PoolPosition { received: number; allocated: number; available: number }
export interface VolumeSummary {
  rates: WorkRate[]; done: Record<string, number>; waiting: Record<string, number>; expectedClientDebt: number;
  counts: Record<'NEW' | 'REVIEW' | 'FREE' | 'LINKED', number>;
  pool: { organizationId: string; organizationName: string; positions: Record<string, PoolPosition> }[];
}
export interface PickLine { code: string; name: string; unit: string; qty: number; price: number; amount: number; role: RateRole; available: number | null }
export interface VolumePayout {
  id: string; date: string; amount: number; organizationId: string; counterpartyId: string; counterpartyName: string;
  projectId: string; crmStatus: 'SENT' | 'ERROR' | 'DELETED' | 'SKIPPED' | null; crmError: string | null;
  lines: { name: string; qty: number; unit: string; amount: number; code: string | null }[];
}

// Положение подписи на линии в PDF «с подписью» (facsimile.py): сдвиг вправо и вниз, ширина — в мм
export interface SignatureLayout { dx: number; dy: number; width: number; }
